import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

// "I've already sent payment" — for money moving outside Stripe (direct ACH,
// wire, check). Holds reminders and the day-6 pause while it settles. One
// report per invoice; Lead Gurus billing is emailed to check the bank.
// `submit(form)` returns the API promise; the caller decides which endpoint.
export default function ReportPaymentDialog({ open, invoiceNumber, submit, onClose, onReported }) {
  const [form, setForm] = useState({ payment_method: 'ach_wire', sent_on: '', reference: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ payment_method: 'ach_wire', sent_on: dayjs().format('YYYY-MM-DD'), reference: '' });
      setError('');
    }
  }, [open]);

  const handleSubmit = () => {
    setSubmitting(true);
    setError('');
    submit(form)
      .then((res) => onReported(res?.data?.expires_on || true))
      .catch((err) => {
        console.error('Unable to report payment', err);
        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.sent_on?.[0] ||
            'We could not record that. Please try again.',
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={() => (submitting ? null : onClose())} maxWidth="xs" fullWidth>
      <DialogTitle>Already sent this payment?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {invoiceNumber ? `Invoice ${invoiceNumber}. ` : ''}
            Bank transfers and checks take a few days to reach us and don&apos;t show up here
            straight away. Tell us it&apos;s on the way and we&apos;ll stop the reminders while we
            wait.
          </Typography>
          <TextField
            select
            label="How did you pay?"
            value={form.payment_method}
            onChange={(event) => setForm((prev) => ({ ...prev, payment_method: event.target.value }))}
            fullWidth
            size="small"
          >
            <MenuItem value="ach_wire">ACH or wire</MenuItem>
            <MenuItem value="check">Check</MenuItem>
            <MenuItem value="other">Something else</MenuItem>
          </TextField>
          <TextField
            type="date"
            label="Date sent"
            value={form.sent_on}
            onChange={(event) => setForm((prev) => ({ ...prev, sent_on: event.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
          <TextField
            label="Reference or check number (optional)"
            value={form.reference}
            onChange={(event) => setForm((prev) => ({ ...prev, reference: event.target.value }))}
            fullWidth
            size="small"
            helperText="Helps us match it to your payment faster."
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : 'Let them know'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ReportPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  invoiceNumber: PropTypes.string,
  submit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onReported: PropTypes.func.isRequired,
};
